import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Stack, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Collapse,
  FormControl, InputLabel, Select, MenuItem, Skeleton, Tooltip,
  TablePagination, Divider, Chip, Alert, alpha,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon   from '@mui/icons-material/KeyboardArrowUp';
import RestartAltIcon        from '@mui/icons-material/RestartAlt';
import EmojiEventsIcon       from '@mui/icons-material/EmojiEvents';
import CalendarTodayIcon     from '@mui/icons-material/CalendarToday';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotesIcon             from '@mui/icons-material/Notes';
import { authFetch }         from '../services/apiServices';

const BRAND = '#3E8FA3';

// ── status helpers ────────────────────────────────────────────────────────────
const STATUS_META = {
  'I përfunduar': { key: 'statusCompleted', color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0' },
  'Në pritje':    { key: 'statusPending',   color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
  'Draft':        { key: 'statusDraft',     color: '#64748B', bg: '#F8FAFC', border: '#E2E8F0' },
};
const statusMeta = (s) => STATUS_META[s] || STATUS_META['Draft'];

const fmtDate = (d, locale = 'sq-AL') => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return String(d);
  return dt.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ── KPI tile (app-wide idiom: uppercase label, bold value) ────────────────────
function Kpi({ icon, label, value, color, loading }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, flex: 1, minWidth: 150 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 38, height: 38, borderRadius: 2, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(color, 0.1), color,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {label}
          </Typography>
          {loading
            ? <Skeleton width={64} height={28} />
            : (
              <Typography noWrap sx={{ fontSize: '1.2rem', fontWeight: 800, lineHeight: 1.2, color: 'text.primary' }}>
                {value}
              </Typography>
            )}
        </Box>
      </Stack>
    </Paper>
  );
}

// ── expandable table row ──────────────────────────────────────────────────────
function EvalRow({ row, t }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);   // pikët për kriter, kur ka
  const meta     = statusMeta(row.status);
  const hasNotes = !!(row.notes && String(row.notes).trim());
  /**
   * Një pikë pa shpjegim nuk është përgjigje.
   *
   * Kur vlerësimi është bërë me shabllon, punonjësi duhet të shohë PSE doli ai
   * numër: cili kriter, me çfarë peshe, dhe sa mori. Vlerësimet e vjetra nuk
   * kanë pikë fare — ato mbeten si ishin, me rezultatin me fjalë.
   */
  const hasScore = row.score !== null && row.score !== undefined;
  const canExpand = hasNotes || hasScore;

  useEffect(() => {
    if (!open || !hasScore || detail) return;
    let alive = true;
    authFetch(`/api/evaluations/${row.id}`)
      .then((r) => r.json())
      .then((j) => { if (alive && j?.success !== false) setDetail(j.data); })
      .catch(() => {});   // pa detaje, mbeten shënimet
    return () => { alive = false; };
  }, [open, hasScore, detail, row.id]);

  return (
    <>
      <TableRow
        hover
        onClick={canExpand ? () => setOpen(o => !o) : undefined}
        sx={{
          cursor: canExpand ? 'pointer' : 'default',
          '& > td': { borderBottom: canExpand && open ? 'unset' : undefined },
        }}
      >
        <TableCell sx={{ width: 44, py: 1 }}>
          {canExpand && (
            <Tooltip title={t('myEvaluation.notes')}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}>
                {open
                  ? <KeyboardArrowUpIcon fontSize="small" />
                  : <KeyboardArrowDownIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
        <TableCell sx={{ fontWeight: 700 }}>{row.period || '—'}</TableCell>
        <TableCell sx={{ color: 'text.secondary' }}>{row.year || '—'}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0 }} />
            <Typography variant="body2">{row.resultText || '—'}</Typography>
            {hasScore && (
              <Chip size="small" label={`${Number(row.score)}%`}
                sx={{ height: 20, fontWeight: 800, fontSize: '0.68rem', bgcolor: '#EEF6F8', color: '#3E8FA3' }} />
            )}
          </Stack>
        </TableCell>
        <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {fmtDate(row.evaluationDate)}
        </TableCell>
        <TableCell>
          <Chip
            label={t(`myEvaluation.${meta.key}`, row.status || '—')}
            size="small"
            sx={{
              fontWeight: 700, fontSize: '0.7rem',
              color: meta.color, bgcolor: meta.bg,
              border: `1px solid ${meta.border}`,
            }}
          />
        </TableCell>
      </TableRow>

      {canExpand && (
        <TableRow>
          <TableCell sx={{ py: 0, border: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              {/* Copëtimi i pikës — rresht për rresht, që numri të mbrohet. */}
              {hasScore && (
                <Box sx={{ mx: 1.5, mt: 1.5, p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #EEF2F6' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Si u llogarit
                  </Typography>
                  {!detail ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>Po lexohet…</Typography>
                  ) : (detail.breakdown || []).length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      Ky vlerësim nuk ka kritere të ruajtura.
                    </Typography>
                  ) : (
                    <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                      {detail.breakdown.map((b, i) => (
                        <Stack key={i} direction="row" justifyContent="space-between" spacing={2}>
                          <Typography variant="body2" sx={{ minWidth: 0 }}>{b.criterion}</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {b.value}/{detail.scale?.max ?? 5} · pesha {b.weight}% → {b.points} pikë
                          </Typography>
                        </Stack>
                      ))}
                      <Divider sx={{ my: 0.5 }} />
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>Totali</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                          {Number(row.score)}%
                        </Typography>
                      </Stack>
                    </Stack>
                  )}
                </Box>
              )}
              {hasNotes && (
              <Box sx={{ mx: 1.5, mb: 1.5, mt: 1.5, p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #EEF2F6' }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                  <NotesIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('myEvaluation.notes')}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                  {row.notes}
                </Typography>
              </Box>
              )}
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────
export default function MyEvaluationPage() {
  const { t } = useTranslation();

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [yearFilter, setYear]     = useState('');
  const [periodFilter, setPeriod] = useState('');
  const [page, setPage] = useState(0);
  const [rpp, setRpp]   = useState(25);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    authFetch('/api/my-evaluations')
      .then(r => r.json())
      .then((res) => {
        if (!active) return;
        if (!res?.success) {
          setError(res?.error || t('myEvaluation.loadError'));
          setRows([]);
        } else {
          setRows(res.data || []);
        }
      })
      .catch(() => {
        if (active) setError(t('myEvaluation.loadError'));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [t]);

  const years = useMemo(
    () => [...new Set(rows.map(r => r.year).filter(Boolean))].sort((a, b) => b - a),
    [rows],
  );
  const periods = useMemo(
    () => [...new Set(rows.map(r => r.period).filter(Boolean))],
    [rows],
  );
  const filtered = useMemo(() => rows.filter(r =>
    (!yearFilter   || Number(r.year)  === Number(yearFilter)) &&
    (!periodFilter || r.period        === periodFilter)
  ), [rows, yearFilter, periodFilter]);

  useEffect(() => { setPage(0); }, [yearFilter, periodFilter]);

  const paged        = filtered.slice(page * rpp, page * rpp + rpp);
  const latestRow    = rows[0] || null;
  const uniqueYears  = years.length;
  const hasFilters   = !!(yearFilter || periodFilter);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto', width: '100%', minWidth: 0 }}>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={1.75} alignItems="center" sx={{ mb: 3 }}>
        <Box
          sx={{
            width: 44, height: 44, borderRadius: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(BRAND, 0.1), color: BRAND, flexShrink: 0,
          }}
        >
          <EmojiEventsIcon />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
            {t('myEvaluation.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            {t('myEvaluation.subtitle')}
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
      )}

      {/* ── KPI tiles ────────────────────────────────────────────────────── */}
      {!error && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <Kpi
            icon={<EmojiEventsIcon fontSize="small" />}
            label={t('myEvaluation.evaluations')}
            value={rows.length}
            color={BRAND}
            loading={loading}
          />
          <Kpi
            icon={<CalendarTodayIcon fontSize="small" />}
            label={t('myEvaluation.yearsCount')}
            value={uniqueYears || '—'}
            color="#0EA5E9"
            loading={loading}
          />
          <Kpi
            icon={<CheckCircleOutlineIcon fontSize="small" />}
            label={t('myEvaluation.latestResult')}
            value={latestRow ? `${latestRow.period} ${latestRow.year}` : t('myEvaluation.noLast')}
            color="#16A34A"
            loading={loading}
          />
        </Stack>
      )}

      {/* ── Table card (toolbar with inline filters) ─────────────────────── */}
      <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          sx={{ px: 2.5, py: 1.75 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t('myEvaluation.title')}{' '}
            {!loading && (
              <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                — {t('myEvaluation.showing', { count: filtered.length, total: rows.length })}
              </Typography>
            )}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: { xs: 0, md: 120 }, flex: { xs: 1, md: 'unset' }, width: { md: 120 } }}>
              <InputLabel>{t('myEvaluation.year')}</InputLabel>
              <Select
                label={t('myEvaluation.year')}
                value={yearFilter}
                onChange={e => setYear(e.target.value)}
              >
                <MenuItem value="">{t('myEvaluation.allYears')}</MenuItem>
                {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 0, md: 140 }, flex: { xs: 1, md: 'unset' }, width: { md: 140 } }}>
              <InputLabel>{t('myEvaluation.period')}</InputLabel>
              <Select
                label={t('myEvaluation.period')}
                value={periodFilter}
                onChange={e => setPeriod(e.target.value)}
              >
                <MenuItem value="">{t('myEvaluation.allPeriods')}</MenuItem>
                {periods.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>

            <Tooltip title={t('myEvaluation.resetFilters')}>
              <span>
                <IconButton size="small" onClick={() => { setYear(''); setPeriod(''); }} disabled={!hasFilters}>
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        <Divider />

        <TableContainer>
          <Table size="medium" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow sx={{ '& th': {
                color: 'text.secondary', fontWeight: 700, bgcolor: '#FAFBFC',
                fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em',
                whiteSpace: 'nowrap',
              } }}>
                <TableCell sx={{ width: 44 }} />
                <TableCell>{t('myEvaluation.colPeriod')}</TableCell>
                <TableCell>{t('myEvaluation.colYear')}</TableCell>
                <TableCell>{t('myEvaluation.colResult')}</TableCell>
                <TableCell>{t('myEvaluation.colDate')}</TableCell>
                <TableCell>{t('myEvaluation.colStatus')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={6} sx={{ py: 1.5 }}>
                        <Skeleton height={24} sx={{ borderRadius: 1 }} />
                      </TableCell>
                    </TableRow>
                  ))
                : paged.length === 0
                  ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Box sx={{ py: 7, textAlign: 'center', color: 'text.secondary' }}>
                          <EmojiEventsIcon sx={{ fontSize: 48, opacity: 0.2, mb: 1, display: 'block', mx: 'auto' }} />
                          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                            {t('myEvaluation.empty')}
                          </Typography>
                          <Typography variant="caption">
                            {t('myEvaluation.emptyHint')}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                  : paged.map(r => <EvalRow key={r.id} row={r} t={t} />)
              }
            </TableBody>
          </Table>
        </TableContainer>

        {filtered.length > rpp && (
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rpp}
            onRowsPerPageChange={e => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage={t('myEvaluation.rowsPerPage')}
          />
        )}
      </Paper>
    </Box>
  );
}
